import { useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  AlertCard,
  StatusDonutMini,
  StaggerReveal,
  BrandedEmptyState,
  PageViewerControls,
  toast,
  activityRail,
} from '../../components/index.js';
import './me.css';
import './me.redesign.css';

const DAY_MS = 24 * 60 * 60 * 1000;

const SEV_FOR_NOTIF = (n) => {
  const s = String(n.severity || n.priority || '').toLowerCase();
  if (s.includes('critical') || s.includes('high')) return 'red';
  if (s.includes('medium') || s.includes('amber') || s.includes('warn')) return 'amber';
  if (s.includes('info')) return 'blue';
  return 'blue';
};

const isUnread = (n) => String(n.status || '').toLowerCase() === 'unread';

const notificationTime = (n) => {
  const value = n.createdAt || n.occurredAt || n.lastUpdatedAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const sortNotifications = (a, b) => {
  const unreadDelta = Number(isUnread(b)) - Number(isUnread(a));
  if (unreadDelta !== 0) return unreadDelta;
  return notificationTime(b) - notificationTime(a);
};

const statusForAction = (action, currentStatus) => {
  const statusMap = {
    dismiss: 'dismissed',
    mark_read: 'read',
    mark_unread: 'unread',
    acknowledge: 'acknowledged',
  };
  return statusMap[action] || currentStatus;
};

export function NotificationsPage() {
  const { refresh: refreshCurrentUser, unreadNotificationCount } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 5 });
  const r = useApi('/notifications', {
    query: { limit: pager.pageSize, offset: pager.offset },
  });
  const mut = useMutation();
  const items = (r.data?.data || [])
    .filter((n) => n.status !== 'dismissed')
    .slice()
    .sort(sortNotifications);
  // Unread within the loaded page — drives the "Mark all as read" bulk action,
  // which only ever operates on the notifications currently in view.
  const unreadCount = items.filter(isUnread).length;
  // IMP-006: the TopBar bell and this Inbox previously disagreed because the
  // bell counts ALL unread notifications server-side while the Inbox derived its
  // headline from a single paginated page. Anchor the headline on the same
  // server-side unread total the bell reads (CurrentUserContext), and the true
  // notification total from the list envelope's page.total, so the two agree.
  const totalUnread = Number.isFinite(unreadNotificationCount)
    ? unreadNotificationCount
    : unreadCount;
  const totalNotifications = r.data?.page?.total ?? items.length;

  // Severity rollup — the per-notification severity was previously only used as
  // a thin card accent. Surface it as a StatusDonutMini.
  const sevCounts = items.reduce(
    (acc, n) => {
      acc[SEV_FOR_NOTIF(n)] += 1;
      return acc;
    },
    { red: 0, amber: 0, blue: 0 },
  );
  const sevSegments = [
    { label: 'Critical', count: sevCounts.red, tone: 'brick' },
    { label: 'Warning', count: sevCounts.amber, tone: 'amber' },
    { label: 'Informational', count: sevCounts.blue, tone: 'blue' },
  ];

  // Recency grouping. To preserve the asserted "all unread before all read" DOM
  // order, bucket by read-state first, then label each bucket by the recency of
  // its newest item.
  const now = Date.now();
  const recencyLabel = (n) => {
    const t = notificationTime(n);
    if (!t) return 'earlier';
    const age = now - t;
    if (age < DAY_MS) return 'in the last 24 hours';
    if (age < 7 * DAY_MS) return 'this week';
    return 'earlier';
  };
  const unreadItems = items.filter(isUnread);
  const readItems = items.filter((n) => !isUnread(n));
  const groups = [
    {
      key: 'unread',
      heading: 'Unread',
      recency: unreadItems.length ? recencyLabel(unreadItems[0]) : null,
      rows: unreadItems,
    },
    {
      key: 'read',
      heading: 'Read',
      recency: readItems.length ? recencyLabel(readItems[0]) : null,
      rows: readItems,
    },
  ].filter((g) => g.rows.length > 0);

  async function act(n, action) {
    try {
      await mut.run(() => api.post(`/notifications/${n.id}/actions`, { action }));
      r.setData?.((prev) => {
        if (!prev?.data) return prev;
        return {
          ...prev,
          data: prev.data.map((x) =>
            x.id === n.id ? { ...x, status: statusForAction(action, x.status) } : x,
          ),
        };
      });
      await refreshCurrentUser?.();
      toast.success({ title: `Notification ${action}`, body: n.title || n.body || n.id });
      activityRail.push({
        action: `notification.${action}`,
        targetType: 'notification',
        targetId: n.id,
        actorUserId: 'you',
      });
    } catch (e) {
      toast.error({ title: 'Could not update', body: e.message });
    }
  }

  async function bulkAct(action, nextStatus, successTitle) {
    const targets = action === 'mark_read' ? items.filter(isUnread) : items;
    if (targets.length === 0) return;
    try {
      await mut.run(() =>
        Promise.all(targets.map((n) => api.post(`/notifications/${n.id}/actions`, { action }))),
      );
      const ids = new Set(targets.map((n) => n.id));
      r.setData?.((prev) => {
        if (!prev?.data) return prev;
        return {
          ...prev,
          data: prev.data.map((x) => (ids.has(x.id) ? { ...x, status: nextStatus } : x)),
        };
      });
      await refreshCurrentUser?.();
      toast.success({ title: successTitle, body: `${targets.length} notification(s) updated.` });
      activityRail.push({
        action: `notification.${action}.bulk`,
        targetType: 'notification',
        targetId: 'bulk',
        actorUserId: 'you',
        summary: `${targets.length} notification(s) updated`,
      });
    } catch (e) {
      toast.error({ title: 'Could not update notifications', body: e.message });
    }
  }

  if (r.loading)
    return (
      <>
        <PageHeader title="Notifications" />
        <LoadingSkeleton rows={5} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;

  return (
    <>
      <PageHeader
        eyebrow="Me · Notifications"
        title="Inbox"
        subtitle="Severity-tinted alerts with deep-links to the source artifact."
        actions={
          <div className="notification-header-actions">
            <span className="notification-count-cue" data-testid="notification-count-cue">
              {totalUnread} unread · {totalNotifications} total
            </span>
            <button
              className="btn ghost"
              onClick={() => bulkAct('mark_read', 'read', 'Unread notifications marked read')}
              disabled={mut.running || unreadCount === 0}
            >
              Mark all as read
            </button>
          </div>
        }
      />
      {items.length === 0 ? (
        <BrandedEmptyState
          icon="✓"
          eyebrow="Inbox clear"
          title="No notifications"
          description="You're all caught up — new alerts will appear here with severity tint and a deep-link to the source artifact."
          tone="sage"
        />
      ) : (
        <div className="notification-stack">
          <section className="me-redesign-section me-split-band">
            <div className="card">
              <span className="eyebrow">Read posture</span>
              <h3 className="card-title">
                {totalUnread} unread of {totalNotifications}
              </h3>
              <p className="card-subtitle">
                Matches the topbar bell. Unread items are listed first, newest at the top.
              </p>
            </div>
            <div className="card me-donut-card">
              <h3 className="card-title">By severity</h3>
              <StatusDonutMini
                segments={sevSegments}
                size={120}
                totalLabel="Alerts"
                ariaLabel="Notifications by severity"
              />
            </div>
          </section>

          {groups.map((group) => (
            <div key={group.key} className="notif-group">
              <div className="notif-group__head">
                <span className="eyebrow">{group.heading}</span>
                <span className="notif-group__count">
                  {group.recency
                    ? `${group.rows.length} · newest ${group.recency}`
                    : group.rows.length}
                </span>
              </div>
              <StaggerReveal className="notification-stack">
                {group.rows.map((n) => {
                  const unread = isUnread(n);
                  return (
                    <AlertCard
                      key={n.id}
                      severity={SEV_FOR_NOTIF(n)}
                      className={`${unread ? 'notification-unread' : 'notification-read'} tone-${SEV_FOR_NOTIF(n)}`}
                      testId="notification-card"
                      dataState={unread ? 'unread' : 'read'}
                      statusLabel={unread ? 'Unread' : 'Read'}
                      statusTone={unread ? 'blue' : 'grey'}
                      title={n.title || n.body || n.id}
                      body={n.body || n.summary || n.notificationType || ''}
                      chips={[n.relatedEntityType, n.relatedEntityId, n.id].filter(Boolean)}
                      meta={n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                      actionLabel={n.actionLabel || 'View source'}
                      secondaryActions={
                        <button
                          className="btn ghost sm"
                          data-testid={
                            unread ? 'notification-mark-read' : 'notification-mark-unread'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            act(n, unread ? 'mark_read' : 'mark_unread');
                          }}
                          disabled={mut.running}
                        >
                          {unread ? 'Mark read' : 'Mark unread'}
                        </button>
                      }
                      onAction={() => {
                        if (n.actionRoute) window.location.href = n.actionRoute;
                        else act(n, 'acknowledge');
                      }}
                    />
                  );
                })}
              </StaggerReveal>
            </div>
          ))}
          <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
            <button
              className="btn ghost"
              onClick={() => bulkAct('dismiss', 'dismissed', 'Notifications dismissed')}
              disabled={mut.running || items.length === 0}
            >
              Dismiss all
            </button>
          </div>
          <PageViewerControls
            page={r.data?.page}
            label="notifications"
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </div>
      )}
    </>
  );
}
